import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";

import { BoostedPayLabel } from "../../Pay/BoostedPayLabel";
import { HourlyPayLabel } from "../../Pay/HourlyPayLabel";
import { TotalPayLabel } from "../../Pay/TotalPayLabel";
import { InstantPayIndicator } from "../../WorkerShifts/components/InstantPayIndicator";
import { type PayBoostEligibilityResult } from "../PayBoost/getPayBoostEligibility";
import { SubsidizedPayLabel } from "../PayBoost/SubsidizedPayLabel";
import { type ShiftBoost } from "../ShiftBoost/api/schema";
import { type ModalShiftOfferData, type ShiftFormattedPayBreakdown } from "../types";

interface FooterPayLabelsProps {
  shiftAuctionPayBreakdown: ShiftFormattedPayBreakdown | undefined;
  offer: ModalShiftOfferData | undefined;
  isShiftAlreadyBooked: boolean;
  isInstantPay: boolean | undefined;
  availableShiftBoost: ShiftBoost | undefined;
  minutesElapsed: number;
  payBoostEligibility?: PayBoostEligibilityResult;
}

export function FooterPayLabels(props: FooterPayLabelsProps) {
  const {
    shiftAuctionPayBreakdown,
    offer,
    isShiftAlreadyBooked,
    isInstantPay,
    availableShiftBoost,
    minutesElapsed,
    payBoostEligibility,
  } = props;

  const subsidizedOriginalRate =
    isDefined(payBoostEligibility) &&
    payBoostEligibility.isEligible &&
    isDefined(payBoostEligibility.originalHourlyRate)
      ? payBoostEligibility.originalHourlyRate
      : undefined;

  return (
    <Stack spacing={3} sx={{ paddingLeft: 5 }}>
      {isDefined(shiftAuctionPayBreakdown) && (
        <>
          <HourlyPayLabel size="large" hourlyPay={shiftAuctionPayBreakdown.hourlyPay} />
          {isInstantPay && <InstantPayIndicator />}
        </>
      )}
      {isDefined(offer) && !isShiftAlreadyBooked && !isDefined(shiftAuctionPayBreakdown) && (
        <>
          {isDefined(subsidizedOriginalRate) ? (
            <SubsidizedPayLabel
              originalRate={formatDollarsAsUsd(subsidizedOriginalRate)}
              subsidizedRate={offer.attributes.formattedHourlyPay}
              size="medium"
            />
          ) : (
            <HourlyPayLabel size="large" hourlyPay={offer.attributes.formattedHourlyPay} />
          )}
          <Stack direction="row" spacing={2} alignItems="center">
            {isDefined(availableShiftBoost) ? (
              <BoostedPayLabel
                size="medium"
                totalPayBeforeBoost={offer.attributes.formattedTotalPay}
                totalWithBoostPay={formatDollarsAsUsd(
                  offer.attributes.totalPay +
                    availableShiftBoost.attributes.amount -
                    (minutesElapsed / 60) * offer.attributes.hourlyPay
                )}
              />
            ) : (
              <TotalPayLabel
                size="medium"
                totalPay={formatDollarsAsUsd(
                  offer.attributes.totalPay - (minutesElapsed / 60) * offer.attributes.hourlyPay
                )}
              />
            )}
            {isInstantPay && <InstantPayIndicator />}
          </Stack>
        </>
      )}
    </Stack>
  );
}
